import type {
  AnswerResult,
  DailySession,
  Game,
  CompletionReward,
  HomeResponse,
  ProfileId,
  RelaySession,
  StudyCard,
  TodayProgress,
} from "./types";
import { getFamilyToken, setFamilyToken } from "./auth";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ??
  "/api";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { profileId?: ProfileId } = {},
): Promise<T> {
  const { profileId, headers, ...rest } = init;
  const token = getFamilyToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    signal: rest.signal ?? AbortSignal.timeout(10_000),
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(profileId ? { "x-profile-id": profileId } : {}),
      ...headers,
    },
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => null) as { error?: string } | null;
    if (res.status === 401 && path !== "/auth/login") setFamilyToken(null);
    throw new ApiError(payload?.error || res.statusText, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  health: () => request<{ ok: true; now: string }>("/health"),

  auth: {
    login: (access_code: string) =>
      request<{ token: string; expires_at: number }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ access_code }),
      }),
    me: () => request<{ household: "duomatt"; expires_at: number }>("/auth/me"),
  },

  listProfiles: () =>
    request<{ profiles: { id: ProfileId; name: string; color: string }[] }>(
      "/profiles",
    ),

  session: {
    today: (profileId: ProfileId, game: Game, date: string) =>
      request<DailySession>(
        `/session/today?game=${game}&date=${date}`,
        { profileId },
      ),

    answer: (
      profileId: ProfileId,
      body: {
        session_id: string;
        item_id: string;
        user_answer: string;
        request_id: string;
      },
    ) =>
      request<AnswerResult>("/session/answer", {
        profileId,
        method: "POST",
        body: JSON.stringify(body),
      }),

    complete: (profileId: ProfileId, session_id: string) =>
      request<CompletionReward>("/session/complete", {
        profileId,
        method: "POST",
        body: JSON.stringify({ session_id }),
      }),
  },

  stats: {
    home: () => request<HomeResponse>("/stats/home"),
    today: (profileId: ProfileId, date: string) =>
      request<Record<Game, TodayProgress>>(
        `/stats/today?date=${date}`,
        { profileId },
      ),
  },

  study: {
    queue: (profileId: ProfileId, date: string) =>
      request<{ cards: StudyCard[] }>(`/study/queue?date=${date}`, { profileId }),
    rate: (
      profileId: ProfileId,
      body: {
        item_key: string;
        game: Game;
        rating: "knew" | "still_learning";
        date_key: string;
      },
    ) =>
      request<{ ok: true }>("/study/rate", {
        profileId,
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  relay: {
    today: (profileId: ProfileId, date: string) =>
      request<RelaySession>(`/relay/today?date=${date}`, { profileId }),
    answer: (
      profileId: ProfileId,
      body: {
        date_key: string;
        request_id: string;
        item_index: number;
        attempt: 0 | 1;
        user_answer: string;
      },
    ) => request<{ correct: boolean; correct_answer: string; already: boolean }>(
      "/relay/answer",
      { profileId, method: "POST", body: JSON.stringify(body) },
    ),
    complete: (profileId: ProfileId, date_key: string) =>
      request<{ relay: RelaySession; xp_gain: number; unlocked: string[] }>(
        "/relay/complete",
        { profileId, method: "POST", body: JSON.stringify({ date_key }) },
      ),
  },
};
